import api from './api';

// ── Types ────────────────────────────────────────────────────────

export interface AIMessageResponse {
  id?: number;
  role: string;
  content: string;
  function_name?: string;
  ai_confidence?: number;
  created_at?: string;
}

export interface AISuggestedAction {
  label: string;
  message: string;
}

export interface AIChatResponse {
  session_id: string;
  message: AIMessageResponse;
  suggested_actions?: AISuggestedAction[];
  status: string;
  resolved: boolean;
  needs_escalation: boolean;
}

export interface AIChatRequest {
  message: string;
  session_id?: string | null;
  context?: Record<string, unknown>;
}

export interface AIFeedbackRequest {
  session_id: string;
  satisfaction: number;
  feedback_text?: string;
}

export interface AIConversation {
  id: number;
  session_id: string;
  channel: string;
  status: string;
  user_id?: number;
  resolved_by_ai: boolean;
  escalated: boolean;
  total_messages: number;
  ai_confidence_avg?: number;
  user_satisfaction?: number;
  created_at: string;
  ended_at?: string;
  messages?: AIMessageResponse[];
}

export interface AIStats {
  total_conversations: number;
  total_chat: number;
  total_voice: number;
  resolved_by_ai: number;
  escalated: number;
  avg_satisfaction?: number;
  avg_confidence?: number;
  avg_messages_per_conversation: number;
  resolution_rate_percent: number;
  today_conversations: number;
  today_resolved: number;
}

export interface WebCallResponse {
  call_id: string;
  access_token: string;
  agent_id: string;
  sample_rate: number;
}

// ── Service ──────────────────────────────────────────────────────

const aiSupportService = {
  /**
   * Send a chat message to the AI support agent.
   * Works for both authenticated and guest users.
   */
  chat: async (data: AIChatRequest): Promise<AIChatResponse> => {
    const response = await api.post<AIChatResponse>('/ai-support/chat', data);
    return response.data;
  },

  /**
   * Close an AI chat conversation
   */
  closeChat: async (sessionId: string): Promise<void> => {
    await api.post('/ai-support/chat/close', null, { params: { session_id: sessionId } });
  },

  /**
   * Submit feedback/rating for an AI conversation
   */
  submitFeedback: async (data: AIFeedbackRequest): Promise<void> => {
    await api.post('/ai-support/chat/feedback', data);
  },

  /**
   * Create a web-based voice call with the AI agent.
   * agentName is injected as a dynamic variable so the voice agent
   * introduces itself with the correct bunny name.
   */
  createWebCall: async (agentName?: string): Promise<WebCallResponse> => {
    const response = await api.post<WebCallResponse>('/ai-support/voice/web-call', {
      agent_name: agentName,
    });
    return response.data;
  },

  /**
   * Notify backend that a voice call ended so it can process
   * post-call actions (password reset, ticket creation, etc.)
   */
  notifyCallEnded: async (callId: string): Promise<void> => {
    try {
      await api.post('/ai-support/voice/call-ended', null, {
        params: { call_id: callId }
      });
    } catch (err) {
      console.warn('Failed to notify call-ended:', err);
    }
  },

  // ── Admin Methods ─────────────────────────────────────────────

  /**
   * Get AI support performance statistics
   */
  getStats: async (): Promise<AIStats> => {
    const response = await api.get<AIStats>('/ai-support/admin/stats');
    return response.data;
  },

  /**
   * Get list of AI conversations
   */
  getConversations: async (params?: {
    channel?: string;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<{ conversations: AIConversation[]; total: number; page: number; per_page: number }> => {
    const response = await api.get('/ai-support/admin/conversations', { params });
    return response.data;
  },

  /**
   * Get full conversation detail with messages
   */
  getConversation: async (id: number): Promise<AIConversation> => {
    const response = await api.get<AIConversation>(`/ai-support/admin/conversations/${id}`);
    return response.data;
  },
};

export default aiSupportService;
